import { useCallback } from 'react';

import type { SidekickActingOrganizationScope } from '../api/actingOrganization';
import type { EnsuredSidekickConversation } from '../contexts/SidekickApiContext';
import { useCreateConversation } from './useCreateConversation';
import { useSavedMessageLookup } from './useSavedMessageLookup';

/**
 * Creates the backend conversation an edit branches into. It names the saved
 * copy of the edited question, so the branch keeps the exchanges before it.
 * When the saved question can't be found, the edit still branches, just
 * without the earlier context.
 * @returns A stable callback taking the acting organization scope and the
 * local id of the edited question.
 */
export const useCreateBranchConversation = (): ((
  organizationScope: SidekickActingOrganizationScope,
  editedQuestionId: string
) => Promise<EnsuredSidekickConversation>) => {
  const createConversation = useCreateConversation();
  const { findSavedQuestionId } = useSavedMessageLookup();

  return useCallback(
    async (organizationScope, editedQuestionId) => {
      const editedMessageId = await findSavedQuestionId(editedQuestionId);
      return editedMessageId === undefined
        ? createConversation(organizationScope)
        : createConversation(organizationScope, { editedMessageId });
    },
    [createConversation, findSavedQuestionId]
  );
};
